import React, { useEffect, useRef } from 'react';
import './V3ImageMap.scss';

interface Area {
  id: string;
  type: 'rectangle' | 'polygon' | 'circle';
  coords: string;
  href?: string;
  title?: string;
  category?: number;
  processed_coords?: string;
}

interface LegendItem {
  tag: string;
  category_ids: number[];
}

interface ImageMapData {
  image_url: string;
  has_areas: boolean;
  processed_areas: Area[];
  legend_items: LegendItem[];
}

interface V3ImageMapProps {
  imageMapData?: ImageMapData;
  loading?: boolean;
  error?: string | null;
}

const V3ImageMap: React.FC<V3ImageMapProps> = ({
  imageMapData,
  loading = false,
  error = null
}) => {
  const mapRef = useRef<HTMLMapElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (imageMapData?.has_areas && mapRef.current && imageRef.current) {
      // Initialize image map interactions
      const areas = mapRef.current.querySelectorAll('area');
      areas.forEach(area => {
        area.addEventListener('mouseenter', () => {
          const areaId = area.getAttribute('data-area-id');
          if (areaId) {
            const overlay = document.getElementById(`overlay-${areaId}`);
            if (overlay) {
              overlay.style.display = 'block';
            }
          }
        });

        area.addEventListener('mouseleave', () => {
          const areaId = area.getAttribute('data-area-id');
          if (areaId) {
            const overlay = document.getElementById(`overlay-${areaId}`);
            if (overlay) {
              overlay.style.display = 'none';
            }
          }
        });
      });
    }
  }, [imageMapData]);

  if (loading) {
    return (
      <div className="v3-image-map">
        <div className="d-flex justify-content-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading seating layout...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="v3-image-map">
        <div className="alert alert-warning" role="alert">
          <strong>Image Map Loading Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!imageMapData) {
    return null;
  }

  const getShapeType = (type: string) => {
    if (type === 'rectangle') return 'rect';
    if (type === 'polygon') return 'poly';
    return type;
  };

  return (
    <div className="v3-image-map">
      <div className="image-map-container">
        <h3 className="section-title">
          <i className="fas fa-map-marked-alt me-2"></i>Seating Layout
        </h3>

        <div className="image-map-wrapper">
          <div id="image-map-viewer" className="image-map-viewer">
            <img
              ref={imageRef}
              id="layout-image"
              src={imageMapData.image_url}
              alt="Event Layout"
              useMap="#event-map"
            />
            <div id="area-overlays" className="area-overlays">
              {imageMapData.processed_areas?.map((area) => (
                <div
                  key={area.id}
                  id={`overlay-${area.id}`}
                  className="area-overlay"
                  style={{ display: 'none' }}
                >
                  {area.title}
                </div>
              ))}
            </div>
            {imageMapData.has_areas && (
              <map name="event-map" id="event-map" ref={mapRef}>
                {imageMapData.processed_areas?.map((area) => (
                  <area
                    key={area.id}
                    shape={getShapeType(area.type)}
                    coords={area.processed_coords || area.coords}
                    href={area.href || '#'}
                    title={area.title}
                    data-category-id={area.category}
                    data-area-id={area.id}
                    data-original-coords={area.processed_coords || area.coords}
                    id={area.id}
                  />
                ))}
              </map>
            )}
          </div>

          {imageMapData.legend_items && imageMapData.legend_items.length > 0 && (
            <div className="image-map-legend">
              <div className="legend d-flex flex-wrap">
                {imageMapData.legend_items.map((legendItem, index) => (
                  <div
                    key={index}
                    className="legend-item mb-2 me-2"
                    data-tag={legendItem.tag}
                    data-category-ids={legendItem.category_ids.join(',')}
                  >
                    <span className="legend-color"></span>
                    <span className="legend-label">{legendItem.tag}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default V3ImageMap;

